import APIClient from "@/APIClient";
import { useToast } from "@/composable/useToast";
import type { FileEntity } from "@/types/file.types";
import { useMutation, useQueries, useQuery } from "@tanstack/vue-query";
import { QueryKey } from "./query-keys";
import { computed, type Ref } from "vue";

export const useDownloadFile = () => {
  const { showSuccess, showLoading } = useToast();
  return useMutation({
    mutationFn: async (file: FileEntity) => {
      const notif = showLoading("toast:files.downloading");
      return await APIClient.downloadFile(file.id).then((blob) => {
        const src = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = src;
        link.download = file.originalName;
        link.click();
        URL.revokeObjectURL(src);
        notif.close();
        showSuccess("toast:files.download-success");
      });
    },
  });
};

export type ImageFileError =
  | "FILE_NOT_READY"
  | "FILE_NOT_FOUND"
  | "USER_NOT_AUTHORIZED"
  | "UNKNOWN";

export const useImageFile = (fileId: number) => {
  const query = useQuery({
    queryKey: [QueryKey.FILE, fileId],
    queryFn: async () => {
      const { url } = await APIClient.getFileUrl(fileId);
      return url;
    },
    staleTime: 1000 * 60 * 5,
    refetchInterval: 1000 * 60 * 4,
    retry: false,
  });

  const errorCode = computed<ImageFileError | null>(() => {
    if (!query.isError.value) return null;
    const err = query.error.value as any;
    const code = err?.response?.error;
    if (
      code === "FILE_NOT_READY" ||
      code === "FILE_NOT_FOUND" ||
      code === "USER_NOT_AUTHORIZED"
    ) {
      return code;
    }
    return "UNKNOWN";
  });

  return {
    ...query,
    errorCode,
  };
};

export const useImageFiles = (fileIds: Ref<number[]>) => {
  const queries = useQueries({
    queries: computed(() =>
      fileIds.value.map((id) => ({
        queryKey: [QueryKey.FILE, id],
        queryFn: async () => {
          const { url } = await APIClient.getFileUrl(id);
          return url;
        },
        staleTime: 1000 * 60 * 5,
        refetchInterval: 1000 * 60 * 4,
      })),
    ),
  });

  const isLoading = computed(() => queries.value.some((q) => q.isLoading));
  const isError = computed(() => queries.value.some((q) => q.isError));
  const data = computed(() => queries.value.map((q) => q.data).filter(Boolean));

  return { data, isLoading, isError };
};
